import { useEffect, useRef, useState, useCallback } from "react";
import { POINT_TYPES, COLOR_MAP } from "../utils/mapConstants";
import { createPulsingShape, createStaticShape } from "../utils/shapeDrawers";

export const usePointInteractions = ({
    mapInstance,
    points,
    handleMarkerClick,
    initialZoomRef,
    initialCenterRef,
}) => {
    const [lastClickedId, setLastClickedId] = useState(null);
    const lastClickedIdRef = useRef(null);
    const eventsRegisteredRef = useRef(false);

    useEffect(() => {
        lastClickedIdRef.current = lastClickedId;
    }, [lastClickedId]);

    const isMapReady = useCallback((map) => {
        if (!map) return false;

        try {
            if (!map.getStyle || !map.addSource || !map.addLayer) return false;
            const style = map.getStyle();
            if (!style) return false;
            return true;
        } catch (error) {
            return false;
        }
    }, []);

    const createMarkerImages = useCallback(
        (map) => {
            try {
                if (!map || !map.addImage || !map.hasImage) {
                    return false;
                }

                points.forEach((point) => {
                    try {
                        const rawState = (point.state || "default").toLowerCase();
                        const figurePoint = point.figurePoint || "circle";
                        const shape =
                            POINT_TYPES.find((pt) => pt.key === figurePoint)?.shape ||
                            "circle";
                        const color = COLOR_MAP[rawState] || COLOR_MAP.default;

                        // Define state name are green (not risk)
                        const isGreen = (
                            rawState === "green" ||
                            rawState === "verde"
                        );

                        // Add style pulsating or static point by isGreen
                        const imageId = (
                            isGreen
                                ? `static-${figurePoint}-${rawState}`
                                : `pulsing-${figurePoint}-${rawState}`
                        );

                        if (!map.hasImage(imageId)) {
                            if (isGreen) {
                                map.addImage(imageId, createStaticShape({ shape, color }), {
                                    pixelRatio: 2,
                                });
                            } else {
                                map.addImage(
                                    imageId,
                                    createPulsingShape({ shape, color, mapInstance: map }),
                                    { pixelRatio: 2 }
                                );
                            }
                        }
                    } catch (error) {
                    }
                });

                return true;
            } catch (error) {
                return false;
            }
        },
        [points]
    );

    const createPointLayers = useCallback(
        (map) => {
            try {
                if (
                    !map ||
                    !map.addSource ||
                    !map.addLayer ||
                    !map.getSource ||
                    !map.getLayer
                ) {
                    return false;
                }

                const geojsonData = {
                    type: "FeatureCollection",
                    features: points.map((point) => {
                        const rawState = (point.state || "default").toLowerCase();
                        const figurePoint = point.figurePoint || "circle";

                        // Define state name are green (not risk)
                        const isGreen = (
                            rawState === "green" ||
                            rawState === "verde"
                        );

                        // Add style pulsating or static point by isGreen
                        const iconId = (
                            isGreen
                                ? `static-${figurePoint}-${rawState}`
                                : `pulsing-${figurePoint}-${rawState}`
                        );

                        return {
                            type: "Feature",
                            properties: {
                                id: point.id,
                                name: point.name,
                                iconId: iconId,
                            },
                            geometry: {
                                type: "Point",
                                coordinates: [point.longitude, point.latitude],
                            },
                        };
                    }),
                };

                const sourceId = "pulsing-dot-source";
                if (map.getSource(sourceId)) {
                    map.getSource(sourceId).setData(geojsonData);
                } else {
                    map.addSource(sourceId, {
                        type: "geojson",
                        data: geojsonData,
                    });
                }

                const layerId = "pulsing-dot-layer";
                if (!map.getLayer(layerId)) {
                    map.addLayer({
                        id: layerId,
                        type: "symbol",
                        source: sourceId,
                        layout: {
                            "icon-image": ["get", "iconId"],
                            "icon-allow-overlap": true,
                            "icon-ignore-placement": true,
                        },
                    });
                }

                const labelsLayerId = "pulsing-dot-labels";
                if (!map.getLayer(labelsLayerId)) {
                    map.addLayer({
                        id: labelsLayerId,
                        type: "symbol",
                        source: sourceId,
                        layout: {
                            "text-field": ["get", "name"],
                            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
                            "text-offset": [0, 2],
                            "text-anchor": "top",
                            "text-size": 10,
                        },
                        paint: {
                            "text-color": "#000000",
                            "text-halo-color": "#ffffff",
                            "text-halo-width": 2,
                            "text-opacity": 0.7,
                        },
                    });
                }

                return true;
            } catch (error) {
                return false;
            }
        },
        [points]
    );

    const registerClickEvents = useCallback(
        (map) => {
            if (eventsRegisteredRef.current) {
                return;
            }

            const handleClick = (e) => {
                const feature = e.features[0];
                if (!feature || !feature.properties) return;

                const pointId = feature.properties.id;
                const clickedPoint = points.find((p) => p.id === pointId);
                if (!clickedPoint) return;

                if (lastClickedIdRef.current === pointId) {
                    if (initialCenterRef.current && initialZoomRef.current) {
                        map.flyTo({
                            center: initialCenterRef.current,
                            zoom: initialZoomRef.current,
                            duration: 900,
                        });
                    }

                    setLastClickedId(null);
                    handleMarkerClick(null);
                } else {
                    map.flyTo({
                        center: [clickedPoint.longitude, clickedPoint.latitude],
                        zoom: 17,
                        duration: 900,
                    });

                    setLastClickedId(pointId);
                    handleMarkerClick(clickedPoint);
                }
            };

            const handleMouseEnter = () => {
                map.getCanvas().style.cursor = "pointer";
            };

            const handleMouseLeave = () => {
                map.getCanvas().style.cursor = "";
            };

            map.on("click", "pulsing-dot-layer", handleClick);
            map.on("mouseenter", "pulsing-dot-layer", handleMouseEnter);
            map.on("mouseleave", "pulsing-dot-layer", handleMouseLeave);

            eventsRegisteredRef.current = true;
        },
        [points, handleMarkerClick, initialCenterRef, initialZoomRef]
    );

    const cleanupEvents = useCallback((map) => {
        if (!map || !eventsRegisteredRef.current) return;

        try {
            map.off("click", "pulsing-dot-layer");
            map.off("mouseenter", "pulsing-dot-layer");
            map.off("mouseleave", "pulsing-dot-layer");
            eventsRegisteredRef.current = false;
        } catch (error) {
            eventsRegisteredRef.current = false;
        }
    }, []);

    // Función para limpiar capas y fuentes
    const cleanupLayers = useCallback(
        (map) => {
            if (!map) return;

            try {
                cleanupEvents(map);

                const layersToRemove = ["pulsing-dot-layer", "pulsing-dot-labels"];
                layersToRemove.forEach((layerId) => {
                    try {
                        if (map.getLayer && map.getLayer(layerId)) {
                            map.removeLayer(layerId);
                        }
                    } catch (error) {
                    }
                });

                try {
                    if (map.getSource && map.getSource("pulsing-dot-source")) {
                        map.removeSource("pulsing-dot-source");
                    }
                } catch (error) {
                }

                points.forEach((point) => {
                    try {
                        const rawState = (point.state || "default").toLowerCase();
                        const figurePoint = point.figurePoint || "circle";

                        // Define state name are green (not risk)
                        const isGreen = (
                            rawState === "green" ||
                            rawState === "verde"
                        );

                        // Add style pulsating or static point by isGreen
                        const imageId = (
                            isGreen
                                ? `static-${figurePoint}-${rawState}`
                                : `pulsing-${figurePoint}-${rawState}`
                        );

                        if (map.hasImage && map.hasImage(imageId)) {
                            map.removeImage(imageId);
                        }
                    } catch (error) {
                    }
                });
            } catch (error) {
            }
        },
        [points, cleanupEvents]
    );

    const initializePoints = useCallback(
        (map) => {
            if (!map || !points || points.length === 0) {
                return;
            }

            const attemptInitialization = () => {
                try {
                    if (!isMapReady(map)) {
                        return false;
                    }

                    cleanupLayers(map);

                    const imagesCreated = createMarkerImages(map);
                    if (!imagesCreated) {
                        throw new Error("Images creation failed");
                    }

                    const layersCreated = createPointLayers(map);
                    if (!layersCreated) {
                        throw new Error("Layers creation failed");
                    }

                    registerClickEvents(map);

                    return true;
                } catch (error) {
                    return false;
                }
            };

            const success = attemptInitialization();

            if (!success) {
                const onStyleLoad = () => {
                    map.off("style.load", onStyleLoad);
                    attemptInitialization();
                };

                map.off("style.load", onStyleLoad);
                map.on("style.load", onStyleLoad);
            }
        },
        [
            points,
            cleanupLayers,
            createMarkerImages,
            createPointLayers,
            registerClickEvents,
            isMapReady,
        ]
    );

    // Función para actualizar solo los datos 
    const updatePointsData = useCallback(
        (map) => {
            if (!map || !points || points.length === 0) return;

            try {
                const source = map.getSource("pulsing-dot-source");
                if (source) {
                    const geojsonData = {
                        type: "FeatureCollection",
                        features: points.map((point) => {
                            const rawState = (point.state || "default").toLowerCase();
                            const figurePoint = point.figurePoint || "circle";

                            // Define state name are green (not risk)
                            const isGreen = (
                                rawState === "green" ||
                                rawState === "verde"
                            );

                            // Add style pulsating or static point by isGreen
                            const iconId = (
                                isGreen
                                    ? `static-${figurePoint}-${rawState}`
                                    : `pulsing-${figurePoint}-${rawState}`
                            );

                            return {
                                type: "Feature",
                                properties: {
                                    id: point.id,
                                    name: point.name,
                                    iconId: iconId,
                                },
                                geometry: {
                                    type: "Point",
                                    coordinates: [point.longitude, point.latitude],
                                },
                            };
                        }),
                    };
                    source.setData(geojsonData);
                } else {
                    initializePoints(map);
                }
            } catch (error) {
                initializePoints(map);
            }
        },
        [points, initializePoints]
    );

    const resetSelection = useCallback(() => {
        setLastClickedId(null);
    }, []);

    useEffect(() => {
        if (!mapInstance || !points || points.length === 0) return;

        initializePoints(mapInstance);

        return () => {
            cleanupLayers(mapInstance);
        };
    }, [mapInstance]);

    useEffect(() => {
        if (!mapInstance || !points || points.length === 0) return;
        if (!eventsRegisteredRef.current) return;

        updatePointsData(mapInstance);
    }, [points, updatePointsData]);

    return {
        lastClickedId,
        initializePoints,
        updatePointsData,
        cleanupLayers,
        resetSelection,
    };
};
